import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, CartView, CustomerOffers } from '../api';
import { endpoints } from '../api';
import { Button, ErrorNote } from '../ui';

/**
 * Comp vouchers and season passes at the window (D-373).
 *
 * A voucher is scanned (its QR carries a link with the code; the scanner's
 * keyboard wedge types it in) or typed, for the performance on screen. Its
 * seats go into the sale as comps with the batch's reason and the voucher is
 * used when the sale completes — a sale of nothing but vouchers completes with
 * no tender. A game excluded from vouchers is refused with the reason.
 *
 * A season pass holder attached to the sale can reserve seats for this game:
 * one seat per pass, together, at no charge.
 */
export function VoucherPanel({ performanceId }: { performanceId: string }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => endpoints.addVoucher({ code: code.trim(), performance_id: performanceId }),
    onSuccess: (result) => {
      setError(null);
      setCode('');
      setNotice(`${result.voucher.batch}: ${result.unit_ids.length} comp seat${result.unit_ids.length === 1 ? '' : 's'} for ${result.event.title}. The voucher is used when the sale completes.`);
      queryClient.setQueryData(['cart'], result.cart);
    },
    onError: (cause: ApiError) => {
      setNotice(null);
      setError(cause.message);
    },
  });
  return (
    <section className="rounded-xl border border-line bg-surface">
      <header className="border-b border-line px-5 py-3">
        <h2 className="font-semibold">Comp voucher</h2>
        <p className="text-xs text-ink-600">Scan the voucher's QR or type its code. Good for any one game, once.</p>
      </header>
      <form
        className="flex flex-wrap items-center gap-2 px-5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) add.mutate();
        }}
      >
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Voucher code" aria-label="Voucher code" autoComplete="off" className="touch min-w-0 flex-1 rounded-lg border border-line px-3 font-mono uppercase" />
        <Button type="submit" variant="primary" className="touch" disabled={!code.trim() || add.isPending}>
          Add
        </Button>
      </form>
      {error ? (
        <div className="px-5 pb-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      {notice ? <p className="mx-5 mb-3 rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}
    </section>
  );
}

export function SeasonPassPanel({ performanceId }: { performanceId: string }) {
  const queryClient = useQueryClient();
  const cart = queryClient.getQueryData<CartView>(['cart']);
  const customerSub = cart?.customer?.sub ?? null;
  const offers = useQuery<CustomerOffers, ApiError>({ queryKey: ['customer-offers', customerSub], queryFn: endpoints.customerOffers, enabled: Boolean(customerSub) });
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const passes = offers.data?.season_passes ?? [];
  const selected = chosen ?? passes.map((p) => p.id);
  const reserve = useMutation({
    mutationFn: () => endpoints.redeemSeasonPasses({ pass_ids: selected, performance_id: performanceId }),
    onSuccess: (result) => {
      setError(null);
      queryClient.setQueryData(['cart'], result.cart);
      void queryClient.invalidateQueries({ queryKey: ['customer-offers', customerSub] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  if (!customerSub || passes.length === 0) return null;
  return (
    <section className="rounded-xl border border-line bg-surface">
      <header className="border-b border-line px-5 py-3">
        <h2 className="font-semibold">Season passes</h2>
        <p className="text-xs text-ink-600">One seat per pass for this game, together, at no charge. Playoff games are not covered.</p>
      </header>
      <div className="flex flex-wrap gap-4 px-5 py-3 text-sm">
        {passes.map((pass) => (
          <label key={pass.id} className="flex items-center gap-2">
            <input type="checkbox" checked={selected.includes(pass.id)} onChange={(e) => setChosen(e.target.checked ? [...selected, pass.id] : selected.filter((id) => id !== pass.id))} />
            {pass.label} <span className="font-mono text-xs text-ink-400">{pass.number}</span>
          </label>
        ))}
      </div>
      {error ? (
        <div className="px-5 pb-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      <div className="px-5 pb-3">
        <Button variant="primary" className="touch" disabled={selected.length === 0 || reserve.isPending} onClick={() => reserve.mutate()}>
          Reserve {selected.length} seat{selected.length === 1 ? '' : 's'}
        </Button>
      </div>
    </section>
  );
}
